import {Component, EventEmitter, Input, Output, TemplateRef} from '@angular/core';
import {TableColumn, TableGroup, TablePagination} from '@core/types';
import {NgTemplateOutlet} from '@angular/common';
import {Spinner} from '@core/components/spinner/spinner';
import {animate, state, style, transition, trigger} from '@angular/animations';

@Component({
  selector: 'core-table',
  imports: [
    NgTemplateOutlet,
    Spinner,
  ],
  templateUrl: './table.html',
  styleUrl: './table.scss',
  animations: [
    trigger('detailExpand', [
      state('collapsed,void', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class Table<T> {
  @Input()
  expandableTemplate?: TemplateRef<any>;

  expandedElement: T | null = null;

  @Input()
  loading = false;

  @Input()
  headerHeight?: number;

  @Input()
  headers: TableColumn<T>[] = [];

  @Input()
  showHeader: boolean = true;

  @Input()
  groups?: TableGroup;

  @Input()
  agrupation: boolean = false;

  @Input()
  data: T[] = [];

  @Output()
  onPageChange = new EventEmitter<number>();
  @Output()
  rowToggled = new EventEmitter<{item: T; expanded: boolean}>();

  pages = 0;
  paginationEnabled = false;
  currentPage = 1;
  pageSize = 10;
  initialItemIndex = 0;
  // Paginator
  visiblePages = 7;
  initialSequence: number[] = [];
  middleSequence: number[] = [];
  finalSequence: number[] = [];
  leftDots: boolean = false;
  rightDots: boolean = false;

  _total: number = 0;

  get total() {
    return this._total;
  }

  @Input()
  set total(value: number) {
    this._total = value;
    this.pages = Math.ceil(this.total / this.pageSize);
    if (this.pages > 0) {
      this.calculatePageSequences();
    }
  };

  get groupedHeaders() {
    if (!this.agrupation) return [];

    const grouped: { slug: string, colspan: number }[] = [];

    this.headers.forEach((header, index) => {
      const groupName = header.group || 'default';

      if (index === 0 || groupName !== (this.headers[index - 1].group || 'default')) {
        grouped.push({ slug: groupName, colspan: 1 });
      } else {
        grouped[grouped.length - 1].colspan++;
      }
    });

    return grouped;
  }

  @Input()
  set pagination(pagination: TablePagination) {
    this.paginationEnabled = pagination.enabled;
    this.pageSize = pagination.pageSize || 10;
    this.total = pagination.total || 0;
    this.pages = Math.ceil(this.total / this.pageSize);

    // Calcular secuencias después de establecer pages
    if (this.pages > 0) {
      this.calculatePageSequences();
    }
  }

  @Input()
  set page(page: number) {
    if (this.currentPage !== page) {
      this.currentPage = page;
      this.initialItemIndex = (this.currentPage - 1) * this.pageSize;

      // Solo calcular si ya tenemos páginas
      if (this.pages > 0) {
        this.calculatePageSequences();
      }
    }
  }

  toggleRow(item: T) {
    if (!this.expandableTemplate) return;
    this.expandedElement = this.expandedElement === item ? null : item;
    this.rowToggled.emit({item, expanded: this.expandedElement === item});
  }

  getFieldValue(item: T, field: string): any {
    const itemField = field as keyof T;
    return item[itemField];
  }

  prevPage() {
    if (this.currentPage > 1) {
      const page = this.currentPage - 1;
      this.onPageChange.emit(page);
    }
  }

  nextPage() {
    if (this.currentPage < this.pages) {
      const page = this.currentPage + 1;
      this.onPageChange.emit(page);
    }
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.pages && page !== this.currentPage) {
      this.onPageChange.emit(page);
    }
  }

  calculatePageSequences() {
    if (this.pages <= 0) {
      return;
    }

    const halfVisiblePages = Math.floor(this.visiblePages / 2) - 1;

    const minVisiblePages = 2 * (halfVisiblePages + 1) + 1;

    const initialPageCount: number = Math.floor(halfVisiblePages / 2);
    const middlePageCount: number = halfVisiblePages - initialPageCount;
    const finalPageCount: number = initialPageCount;


    // Si hay pocas páginas, mostrar todas sin dots
    if (this.pages <= minVisiblePages) {
      this.initialSequence = [];
      this.middleSequence = Array.from({length: this.pages}, (_, i) => i + 1);
      this.finalSequence = [];
      this.leftDots = false;
      this.rightDots = false;
      return;
    }

    // Calcular secuencia inicial (primeras n páginas)
    this.initialSequence = Array.from(
      {length: Math.min(initialPageCount, this.pages)},
      (_, i) => i + 1
    );

    // Calcular secuencia final (últimas n páginas)
    this.finalSequence = Array.from(
      {length: Math.min(finalPageCount, this.pages)},
      (_, i) => this.pages - finalPageCount + i + 1
    ).filter(page => page > 0);

    // Calcular secuencia media (n antes y n después de la página actual)
    const middleStart = Math.max(1, this.currentPage - middlePageCount);
    const middleEnd = Math.min(this.pages, this.currentPage + middlePageCount);

    this.middleSequence = Array.from(
      {length: middleEnd - middleStart + 1},
      (_, i) => middleStart + i
    );

    // Verificar y fusionar solapamientos
    const lastInitial = this.initialSequence[this.initialSequence.length - 1] || 0;
    const firstMiddle = this.middleSequence[0] || Infinity;
    const lastMiddle = this.middleSequence[this.middleSequence.length - 1] || 0;
    const firstFinal = this.finalSequence[0] || Infinity;

    // Número de páginas a mostrar cuando hay ambos dots: initial + middle + final
    // Cuando no hay dot, agregamos páginas extra para compensar el espacio del dot
    const basePages = initialPageCount + (2 * middlePageCount + 1) + finalPageCount;

    // Si la secuencia media solapa o está adyacente a la inicial
    if (firstMiddle !== Infinity && firstMiddle - lastInitial <= 1) {
      // Fusionar y expandir hacia la derecha
      // Agregamos páginas extra porque no habrá leftDot
      const maxPage = Math.min(
        this.pages,
        Math.max(
          lastMiddle,
          basePages
        )
      );

      this.initialSequence = Array.from(
        {length: maxPage},
        (_, i) => i + 1
      );
      this.middleSequence = [];
    }

    // Recalcular después de la primera fusión
    const actualLastMiddle = this.middleSequence.length > 0
      ? this.middleSequence[this.middleSequence.length - 1]
      : this.initialSequence[this.initialSequence.length - 1] || 0;

    // Si la secuencia media (o inicial expandida) solapa o está adyacente a la final
    if (firstFinal !== Infinity && firstFinal - actualLastMiddle <= 1) {
      // Fusionar con la final
      if (this.middleSequence.length > 0) {
        // Si había secuencia media, expandir hacia la izquierda
        // Agregamos páginas extra porque no habrá rightDot
        const minPage = Math.max(
          1,
          Math.min(
            this.middleSequence[0],
            this.pages - basePages + 1
          )
        );

        this.middleSequence = Array.from(
          {length: this.pages - minPage + 1},
          (_, i) => minPage + i
        );
        this.finalSequence = [];
      } else {
        // Si no había secuencia media, toda la secuencia inicial va hasta el final
        this.initialSequence = Array.from(
          {length: this.pages},
          (_, i) => i + 1
        );
        this.finalSequence = [];
      }
    }

    // Recalcular valores finales para los dots
    const finalLastInitial = this.initialSequence[this.initialSequence.length - 1] || 0;
    const finalFirstMiddle = this.middleSequence[0] || (this.finalSequence[0] || Infinity);
    const finalLastMiddle = this.middleSequence[this.middleSequence.length - 1] ||
      (this.initialSequence[this.initialSequence.length - 1] || 0);
    const finalFirstFinal = this.finalSequence[0] || Infinity;

    // leftDots solo si hay gap real (diferencia > 1)
    this.leftDots = this.middleSequence.length > 0 &&
      finalFirstMiddle - finalLastInitial > 1;

    // rightDots solo si hay gap real (diferencia > 1)
    this.rightDots = this.finalSequence.length > 0 &&
      finalFirstFinal - finalLastMiddle > 1;
  }
}
